import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { useApp } from '@/app/AppContext'
import { can } from '@/auth/permissions'
import { AccessDenied } from '@/pages/AccessDenied'
import { ApiError } from '@/lib/api'
import { fetchPage } from '@/lists/api'
import { CreateField, CreateModal } from './CreateModal'
import { createDeal, fetchFunnels } from './dealApi'
import { RecordPicker } from './RecordPicker'

/**
 * Заведение сделки из веба (тикет #100), вёрстка по CreateRecord.dc.html (форма `deal`).
 *
 * Только то, что нужно, чтобы сделка встала на доску: название, воронка, клиент, срок.
 * Позиции добавляются уже на карточке — у них своя операция с пересчётом суммы, и форма,
 * собирающая всё сразу, означала бы второй путь к той же логике. Поэтому поля «Сумма» из
 * макета нет: заведение суммы не принимает, она складывается из позиций.
 *
 * Стадия не выбирается: новая сделка встаёт на первую открытую стадию воронки, и поле
 * показывает какую — с вероятностью, которую она получит.
 *
 * У клиента ищутся **открытые сделки** механизмом списков (в области видимости
 * смотрящего): вторая сделка на ту же поставку дробит воронку, и это видно до сохранения.
 * Это подсказка, а не запрет — две параллельные поставки законны.
 *
 * Ответственный необязателен: пустой — значит «я», и сервер ставит того, кто заводит.
 */
export function DealCreatePage() {
  const { t, user } = useApp()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [title, setTitle] = useState('')
  const [funnelId, setFunnelId] = useState('')
  // "New deal" on a counterparty card arrives with the client already chosen: asking for it
  // again is a chance to pick the wrong one from a list of similar names.
  const location = useLocation()
  const [counterparty, setCounterparty] = useState<{ id: string; label: string } | null>(
    () => presetCounterparty(location.state),
  )
  const [expectedCloseOn, setExpectedCloseOn] = useState('')
  const [responsible, setResponsible] = useState<{ id: string; label: string } | null>(null)
  const [currency, setCurrency] = useState('')
  const [separate, setSeparate] = useState(false)
  const [notice, setNotice] = useState<string | null>(null)

  const funnels = useQuery({ queryKey: ['funnels'], queryFn: ({ signal }) => fetchFunnels(signal) })
  // The default funnel, not the first by name: that is where the board opens too.
  const chosenFunnel =
    funnelId || funnels.data?.find((funnel) => funnel.defaultFunnel)?.id || funnels.data?.[0]?.id || ''
  const funnel = funnels.data?.find((candidate) => candidate.id === chosenFunnel)
  const openStages = (funnel?.stages ?? [])
    .filter((stage) => stage.stageType === 'open')
    .sort((a, b) => a.stageOrder - b.stageOrder)
  const firstStage = openStages[0]

  const openDeals = useQuery({
    queryKey: ['create-deal-open', counterparty?.id],
    enabled: Boolean(counterparty) && can(user, 'sales.deal', 'read'),
    retry: false,
    queryFn: ({ signal }) =>
      fetchPage(
        'sales.deal',
        {
          columns: ['number', 'title', 'stage_id', 'expected_close_on'],
          filters: [
            { column: 'counterparty_id', operator: 'eq', value: counterparty!.id },
            { column: 'closed_at', operator: 'is_null' },
          ],
          sort: [{ column: 'created_at', descending: true }],
          pageSize: 3,
        },
        null,
        signal,
      ),
  })
  const existing = openDeals.data?.rows ?? []

  const save = useMutation({
    mutationFn: () =>
      createDeal({
        title: title.trim(),
        funnelId: chosenFunnel,
        counterpartyId: counterparty?.id ?? null,
        currency: currency.trim() === '' ? null : currency.trim().toUpperCase(),
        expectedCloseOn: expectedCloseOn || null,
        responsibleEmployeeId: responsible?.id ?? null,
      }),
    onSuccess: (deal) => {
      void queryClient.invalidateQueries({ queryKey: ['list', 'sales.deal'] })
      void queryClient.invalidateQueries({ queryKey: ['deal-board'] })
      navigate(`/deals/${deal.id}`)
    },
    onError: (error) => setNotice(error instanceof ApiError ? error.message : t('app.error')),
  })

  if (!can(user, 'sales.deal', 'create')) return <AccessDenied />

  const blockedReason = title.trim() === ''
    ? t('deal.create.needTitle')
    : chosenFunnel === ''
      ? t('deal.create.needFunnel')
      : null

  return (
    <CreateModal
      overline={t('nav.deals')}
      title={t('deal.create.title')}
      error={notice}
      blockedReason={blockedReason}
      pending={save.isPending}
      submitLabel={save.isPending ? t('create.saving') : t('deal.create.submit')}
      onCancel={() => navigate('/deals')}
      onSubmit={() => {
        setNotice(null)
        save.mutate()
      }}
      more={{
        content: (
          <CreateField id="deal-currency" label={t('column.currency')} help={t('deal.create.currencyHelp')}>
            <input id="deal-currency" className="input mono" maxLength={3} value={currency}
                   placeholder="RUB" onChange={(event) => setCurrency(event.target.value)} />
          </CreateField>
        ),
      }}
    >
      <div className="form-grid">
        <CreateField id="deal-counterparty" label={t('column.counterparty_id')} span>
          <RecordPicker id="deal-counterparty" entity="sales.counterparty" searchColumn="name"
                        extraColumn="inn" value={counterparty} onChange={(value) => {
                          setCounterparty(value)
                          setSeparate(false)
                        }} />
        </CreateField>

        {existing.length > 0 && !separate && (
          <div className="dup span-2" role="status">
            <svg className="i ico" viewBox="0 0 16 16" aria-hidden="true" style={{ justifySelf: 'center', alignSelf: 'start', marginTop: 2 }}>
              <path d="M2.5 2.5h3v11h-3zM6.5 2.5h3v7h-3zM10.5 2.5h3v9h-3z" />
            </svg>
            <div className="stack" style={{ gap: 6 }}>
              <span className="strong">{t('deal.create.openDeals', { count: existing.length })}</span>
              <ul className="dup-list">
                {existing.map((row) => (
                  <li key={row.id}>
                    <Link to={`/deals/${row.id}`}>{String(row.title ?? row.number ?? row.id)}</Link>
                    <span className="t-sm secondary">
                      <span className="mono">{String(row.number ?? '')}</span>
                      {row.stage_id__label ? <> · {String(row.stage_id__label)}</> : null}
                    </span>
                  </li>
                ))}
              </ul>
              <span className="row-wrap" style={{ gap: 6 }}>
                <button type="button" className="btn btn-ghost btn-sm" onClick={() => setSeparate(true)}>
                  {t('deal.create.separate')}
                </button>
              </span>
            </div>
          </div>
        )}
        {existing.length > 0 && separate && (
          <div className="alert alert-neutral span-2">
            <span style={{ flex: 1 }}>{t('deal.create.separateNote')}</span>
            <button type="button" className="btn btn-ghost btn-sm" onClick={() => setSeparate(false)}>
              {t('card.cancel')}
            </button>
          </div>
        )}

        <CreateField id="deal-title" label={t('column.title')} required span>
          <input id="deal-title" className="input" value={title} required maxLength={512}
                 onChange={(event) => setTitle(event.target.value)} />
        </CreateField>
        <CreateField id="deal-funnel" label={t('column.funnel_id')}>
          <select id="deal-funnel" className="input" value={chosenFunnel}
                  onChange={(event) => setFunnelId(event.target.value)}>
            {(funnels.data ?? []).map((candidate) => (
              <option key={candidate.id} value={candidate.id}>
                {candidate.name}
              </option>
            ))}
          </select>
        </CreateField>
        <CreateField
          label={t('column.stage_id')}
          help={firstStage ? t('deal.create.stageHelp', { probability: firstStage.probability }) : undefined}
        >
          <span className="input is-readonly create-stage">
            {firstStage ? (
              <>
                <span className="pips" aria-hidden="true">
                  {openStages.map((stage) => (
                    <i key={stage.id} className={stage.id === firstStage.id ? 'on' : undefined} />
                  ))}
                </span>
                {firstStage.name}
              </>
            ) : (
              <span className="muted">—</span>
            )}
          </span>
        </CreateField>
        <CreateField id="deal-close" label={t('column.expected_close_on')}>
          <input id="deal-close" className="input" type="date" value={expectedCloseOn}
                 onChange={(event) => setExpectedCloseOn(event.target.value)} />
        </CreateField>
        <CreateField
          id="deal-responsible"
          label={t('column.responsible_employee_id')}
          help={!responsible ? t('deal.create.responsibleMe') : undefined}
        >
          <RecordPicker id="deal-responsible" entity="hr.employee" searchColumn="last_name"
                        extraColumn="first_name" value={responsible} onChange={setResponsible} />
        </CreateField>
      </div>
    </CreateModal>
  )
}

/**
 * Клиент, переданный карточкой контрагента («Новая сделка»).
 *
 * Состояние навигации приходит без типа — из истории браузера, — поэтому проверяется
 * форма, а не принимается на веру: битое значение означает «клиент не выбран», а не падение.
 */
function presetCounterparty(state: unknown): { id: string; label: string } | null {
  if (typeof state !== 'object' || state == null) return null
  const preset = (state as { counterparty?: unknown }).counterparty
  if (typeof preset !== 'object' || preset == null) return null
  const { id, label } = preset as { id?: unknown; label?: unknown }
  return typeof id === 'string' && typeof label === 'string' ? { id, label } : null
}
